import { getExpressAPIHost } from '../utils';

const REGION_CACHE_KEY = 'mm.region';
const AUTH_REGION_CACHE_KEY = 'mm.auth.region';
const AUTH_CORESYSTEM_CACHE_KEY = 'mm.auth.coreSystem';

/**
 * In-flight promise deduplication.
 * When multiple callers invoke fetchRegionFromBackend concurrently (App.js, Login,
 * getUserRegion, getUser), they share one fetch() call instead of each firing their own.
 * This prevents thundering herd when Express is slow or processing multiple startup requests.
 */
let inflightPromise = null;
let authInflightPromise = null;

const normalizeValue = (value, fieldName) => {
    if (typeof value !== 'string') {
        throw new Error(`${fieldName} not found in response`);
    }

    const normalized = value.trim().toUpperCase();
    if (!normalized) {
        throw new Error(`${fieldName} not found in response`);
    }

    return normalized;
};

/**
 * Fetches the region configuration from the backend API
 * This is used to determine the correct coreSystem for authentication
 * in dev/staging environments where the URL doesn't match the actual region.
 *
 * The region is cached in sessionStorage to avoid repeated API calls.
 * Concurrent callers share one in-flight request via the inflightPromise pattern.
 *
 * @returns {Promise<string>} The region code (e.g., 'UDA', 'MMPA', etc.)
 */
export const fetchRegionFromBackend = async () => {
    const cachedRegion = sessionStorage.getItem(REGION_CACHE_KEY);
    if (cachedRegion) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[RegionConfig] Using cached region:', cachedRegion);
        }
        return cachedRegion;
    }

    if (inflightPromise) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[RegionConfig] Joining in-flight region fetch');
        }
        return inflightPromise;
    }

    inflightPromise = (async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout

        try {
            const apiHost = getExpressAPIHost();
            const response = await fetch(`${apiHost}/region-config`, { signal: controller.signal });

            if (!response.ok) {
                throw new Error(`Failed to fetch region config: ${response.status}`);
            }

            const data = await response.json();
            const region = normalizeValue(data?.region, 'Region');

            sessionStorage.setItem(REGION_CACHE_KEY, region);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('[RegionConfig] Fetched and cached region:', region);
            }
            return region;
        } catch (error) {
            // eslint-disable-next-line no-console
            if (process.env.NODE_ENV !== 'production') {
                console.error('[RegionConfig] Error fetching region from backend:', error);
            }
            const { getRegion } = await import('../utils');
            const fallbackRegion = getRegion(window.location.hostname);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[RegionConfig] Using fallback region from URL:', fallbackRegion);
            }
            return fallbackRegion;
        } finally {
            clearTimeout(timeoutId);
        }
    })().finally(() => {
        inflightPromise = null;
    });

    return inflightPromise;
};

export const fetchAuthConfigFromBackend = async () => {
    const cachedRegion = sessionStorage.getItem(AUTH_REGION_CACHE_KEY);
    const cachedCoreSystem = sessionStorage.getItem(AUTH_CORESYSTEM_CACHE_KEY);

    if (cachedRegion && cachedCoreSystem) {
        return { region: cachedRegion, coreSystem: cachedCoreSystem };
    }

    if (authInflightPromise) {
        return authInflightPromise;
    }

    authInflightPromise = (async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        try {
            const apiHost = getExpressAPIHost();
            const response = await fetch(`${apiHost}/region-config`, { signal: controller.signal });

            if (!response.ok) {
                throw new Error(`Failed to fetch region config: ${response.status}`);
            }

            const data = await response.json();
            const region = normalizeValue(data?.region, 'Region');
            const coreSystem = normalizeValue(data?.coreSystem ?? data?.region, 'Core system');

            sessionStorage.setItem(AUTH_REGION_CACHE_KEY, region);
            sessionStorage.setItem(AUTH_CORESYSTEM_CACHE_KEY, coreSystem);

            return { region, coreSystem };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[RegionConfig] Error fetching auth config from backend:', error);
            }
            const { getRegion } = await import('../utils');
            const fallbackRegion = getRegion(window.location.hostname);
            return { region: fallbackRegion, coreSystem: fallbackRegion };
        } finally {
            clearTimeout(timeoutId);
        }
    })().finally(() => {
        authInflightPromise = null;
    });

    return authInflightPromise;
};

export const cacheAuthenticatedRegion = (region) => {
    if (typeof region === 'string' && region.trim()) {
        const normalized = region.trim().toUpperCase();
        sessionStorage.setItem(REGION_CACHE_KEY, normalized);
        // Also cache in localStorage so new browser tabs/pages (e.g. Playwright
        // context.newPage()) inherit the region. sessionStorage is NOT shared
        // across new pages in the same browser context.
        localStorage.setItem(REGION_CACHE_KEY, normalized);
    }
    sessionStorage.removeItem(AUTH_REGION_CACHE_KEY);
    sessionStorage.removeItem(AUTH_CORESYSTEM_CACHE_KEY);
};

/**
 * Clears the cached region from sessionStorage
 * This should be called on logout to ensure fresh region fetch on next login
 */
export const clearCachedRegion = () => {
    sessionStorage.removeItem(REGION_CACHE_KEY);
    sessionStorage.removeItem(AUTH_REGION_CACHE_KEY);
    sessionStorage.removeItem(AUTH_CORESYSTEM_CACHE_KEY);
    localStorage.removeItem(REGION_CACHE_KEY);
    // eslint-disable-next-line no-console
    console.log('[RegionConfig] Cleared cached region');
};
